import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { useAuthStore } from "../store/auth";
import { api, logout as apiLogout } from "../lib/api";
import { SmartImg } from "../components/SmartImg";
import { SakuraField } from "../components/SakuraField";
import { LanguageMenu } from "../components/LanguageMenu";
import { Animal3DCanvas } from "../components/3d/Animal3DCanvas";
import { DressupStudio } from "../components/3d/DressupStudio";

type LevelInfo = {
  level: number;
  label: string;
  xp_in_level: number;
  xp_to_next: number;
  progress_pct: number;
  is_max: boolean;
};

type Profile = {
  id: number;
  name: string;
  total_xp: number;
  current_level: number;
  coin_balance: number;
  streak_days: number;
  unlocked_areas: string[];
  earned_badges: string[];
  level: LevelInfo;
};

export function HomePage() {
  const { t } = useTranslation(["common", "learner"]);
  const { user } = useAuthStore();

  const [showDressup, setShowDressup] = useState(false);

  const { data: profile } = useQuery({
    queryKey: ["profile"],
    queryFn: () => api<Profile>("/api/learner/profile"),
  });

  return (
    <>
      {/* ============================================================ */}
      {/* Mobile / Tablet : vertical-scroll flow                       */}
      {/* ============================================================ */}
      <div className="relative w-full min-h-screen lg:hidden flex flex-col">
        <SmartImg
          src="/assets/areas/room_self_room.png"
          alt="My Room"
          className="absolute inset-0 w-full h-full object-cover"
          fallback={<div className="absolute inset-0 room-fallback" />}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-white/50 to-white/80" />
        <SakuraField />

        {/* Header */}
        <header className="relative z-40 flex items-center justify-between gap-2 p-3">
          <div className="bg-white/90 backdrop-blur rounded-xl shadow px-3 py-2 min-w-0 flex-1">
            <div className="text-[10px] text-muted leading-tight">{t("common:app_name")}</div>
            <div className="text-base font-bold text-primary truncate">{user?.name} さん</div>
          </div>
          <button
            onClick={() => setShowDressup(true)}
            aria-label={t("learner:student_closet")}
            className="bg-white/90 backdrop-blur rounded-xl shadow px-2.5 h-10 flex items-center gap-1 text-[#8f332c] font-black text-xs shrink-0 border border-[#d9b45f]/40"
          >
            <span>👗</span>
            <span>{t("learner:student_closet")}</span>
          </button>
          <Link
            to="/"
            aria-label={t("learner:map")}
            className="bg-white/90 backdrop-blur rounded-xl shadow w-10 h-10 flex items-center justify-center text-primary shrink-0"
          >
            <MapIcon />
          </Link>
          <Link
            to="/leaderboard"
            aria-label={t("learner:leaderboard")}
            className="bg-white/90 backdrop-blur rounded-xl shadow w-10 h-10 flex items-center justify-center text-yellow-500 shrink-0"
          >
            <TrophyIcon />
          </Link>
          <LanguageMenu variant="compact" />
          <button
            onClick={apiLogout}
            aria-label={t("common:logout")}
            className="bg-white/90 backdrop-blur rounded-xl shadow w-10 h-10 flex items-center justify-center text-muted shrink-0"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
            </svg>
          </button>
        </header>

        {/* Status row */}
        <div className="relative z-20 px-3 pt-2">
          <div className="bg-white/90 backdrop-blur rounded-2xl shadow p-3 flex flex-col gap-2">
            <LevelPanel profile={profile} />
            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-200">
              <Stat label={t("learner:coin")} value={profile?.coin_balance ?? 0} icon="🌸" />
              <Stat
                label={t("learner:streak")}
                value={`${profile?.streak_days ?? 0}${t("learner:days")}`}
                icon="🔥"
              />
            </div>
          </div>
        </div>

        {/* 生徒アバター (3D) */}
        <div className="relative z-20 flex flex-col items-center mt-2 mb-1">
          <div className="w-52 h-60 relative flex items-center justify-center">
            <Animal3DCanvas height={230} autoWave />
            <button
              onClick={() => setShowDressup(true)}
              className="absolute bottom-1 bg-white/95 backdrop-blur-sm border border-[#d9b45f] text-[#8f332c] text-xs font-black px-3.5 py-1.5 rounded-full shadow-sm hover:scale-105 transition flex items-center gap-1.5"
            >
              <span>👗</span>
              <span>{t("learner:student_closet")}</span>
            </button>
          </div>
        </div>

        {/* Object cards */}
        <div className="relative z-20 px-3 pb-6 grid grid-cols-3 gap-2 sm:gap-3 mt-auto">
          <ObjectCard
            to="/kana/hiragana"
            imgSrc="/assets/objects/obj_bookshelf_hiragana.png"
            fallback="📚"
            title="ひらがな"
            subtitle="Hiragana"
            colorClass="from-sakura to-pink-200"
            compact
          />
          <ObjectCard
            to="/kana/katakana"
            imgSrc="/assets/objects/obj_calligraphy_katakana.png"
            fallback="🖌"
            title="カタカナ"
            subtitle="Katakana"
            colorClass="from-blue-200 to-primary/40"
            compact
          />
          <ObjectCard
            to="/trace/hiragana"
            imgSrc="/assets/objects/obj_calligraphy_katakana.png"
            fallback="🖋"
            title="なぞる"
            subtitle="Trace"
            colorClass="from-amber-200 to-orange-300"
            compact
          />
        </div>
      </div>

      {/* ============================================================ */}
      {/* Desktop (lg+) : absolute "diorama" layout                     */}
      {/* ============================================================ */}
      <div className="relative w-full h-screen overflow-hidden hidden lg:block">
        <SmartImg
          src="/assets/areas/room_self_room.png"
          alt="My Room"
          className="absolute inset-0 w-full h-full object-cover"
          fallback={<div className="absolute inset-0 room-fallback" />}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/30" />

        <SakuraField />

        <header className="absolute top-0 left-0 right-0 z-40 flex items-start justify-between p-6">
          <div className="bg-white/90 backdrop-blur rounded-2xl shadow-lg px-6 py-4">
            <div className="text-sm text-muted">{t("common:app_name")}</div>
            <div className="text-xl font-bold text-primary">{user?.name} さん</div>
          </div>
          <div className="flex gap-3">
            {/* きせかえクローゼットボタン */}
            <button
              onClick={() => setShowDressup(true)}
              className="bg-white/90 backdrop-blur rounded-2xl shadow-lg px-5 py-3 text-[#8f332c] flex items-center gap-2 border border-[#d9b45f]/40 hover:bg-amber-50/80 transition"
            >
              <span className="text-xl">👗</span>
              <span className="text-base font-bold">{t("learner:student_closet")}</span>
            </button>
            <Link
              to="/"
              aria-label={t("learner:map")}
              className="bg-white/90 backdrop-blur rounded-2xl shadow-lg px-5 py-3 text-primary flex items-center gap-2"
            >
              <MapIcon />
              <span className="text-base font-bold text-primary">{t("learner:map")}</span>
            </Link>
            <Link
              to="/leaderboard"
              aria-label={t("learner:leaderboard")}
              className="bg-white/90 backdrop-blur rounded-2xl shadow-lg px-5 py-3 text-yellow-500 flex items-center gap-2"
            >
              <TrophyIcon />
              <span className="text-base font-bold text-primary">{t("learner:leaderboard")}</span>
            </Link>
            <LanguageMenu variant="full" />
            <button
              onClick={apiLogout}
              className="bg-white/90 backdrop-blur rounded-2xl shadow-lg px-5 py-3 text-base text-muted"
            >
              {t("common:logout")}
            </button>
          </div>
        </header>

        <aside className="absolute top-28 right-6 z-30 bg-white/90 backdrop-blur rounded-2xl shadow-xl px-6 py-5 flex flex-col gap-3 min-w-[240px]">
          <LevelPanel profile={profile} />
          <div className="border-t border-gray-200" />
          <Stat label={t("learner:coin")} value={profile?.coin_balance ?? 0} icon="🌸" />
          <div className="border-t border-gray-200" />
          <Stat
            label={t("learner:streak")}
            value={`${profile?.streak_days ?? 0}${t("learner:days")}`}
            icon="🔥"
          />
        </aside>

        {/* 中央アバター (生徒の3Dアバター) */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 flex flex-col items-center">
          <div className="w-80 h-96 relative flex items-center justify-center">
            <Animal3DCanvas height={360} autoWave />
            <button
              onClick={() => setShowDressup(true)}
              className="absolute bottom-2 bg-white/95 backdrop-blur border-2 border-[#d9b45f] text-[#8f332c] text-xs font-black px-4 py-2 rounded-full shadow-lg hover:scale-105 transition flex items-center gap-2"
            >
              <span>👗</span>
              <span>{t("learner:student_closet")}</span>
            </button>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 w-full max-w-5xl px-4 grid grid-cols-3 gap-12">
          <ObjectCard
            to="/kana/hiragana"
            imgSrc="/assets/objects/obj_bookshelf_hiragana.png"
            fallback="📚"
            title="ひらがな"
            subtitle="Hiragana"
            colorClass="from-sakura to-pink-200"
          />
          <ObjectCard
            to="/kana/katakana"
            imgSrc="/assets/objects/obj_calligraphy_katakana.png"
            fallback="🖌"
            title="カタカナ"
            subtitle="Katakana"
            colorClass="from-blue-200 to-primary/40"
          />
          <ObjectCard
            to="/trace/hiragana"
            imgSrc="/assets/objects/obj_calligraphy_katakana.png"
            fallback="🖋"
            title="なぞる"
            subtitle="Trace"
            colorClass="from-amber-200 to-orange-300"
          />
        </div>
      </div>

      {/* きせかえスタジオモーダル */}
      <AnimatePresence>
        {showDressup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 overflow-y-auto"
            onClick={() => setShowDressup(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-2xl"
            >
              <DressupStudio onClose={() => setShowDressup(false)} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function Stat({ label, value, icon }: { label: string; value: string | number; icon: string }) {
  return (
    <div className="flex flex-col items-center lg:items-start">
      <div className="text-xl lg:text-2xl">{icon}</div>
      <div className="text-2xl lg:text-3xl font-bold text-primary leading-tight">{value}</div>
      <div className="text-xs lg:text-sm text-muted">{label}</div>
    </div>
  );
}

function LevelPanel({ profile }: { profile?: Profile }) {
  const { t } = useTranslation(["common", "learner"]);
  const lv = profile?.level;
  const label = lv?.label ?? "入門";
  const pct = lv?.progress_pct ?? 0;
  const xpToNext = lv?.xp_to_next ?? 0;
  const isMax = lv?.is_max ?? false;
  return (
    <div>
      <div className="text-[10px] lg:text-xs text-muted">{t("learner:level")}</div>
      <div className="flex items-baseline gap-2">
        <div className="text-2xl lg:text-3xl font-bold text-primary leading-tight">{label}</div>
        <div className="text-xs lg:text-sm text-muted">{pct}%</div>
      </div>
      <div className="mt-1.5 lg:mt-2 h-1.5 lg:h-2 w-full bg-gray-200 rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-sakura to-primary transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      <div className="text-[10px] lg:text-xs text-muted mt-1">
        {isMax ? t("learner:max_level") : t("learner:to_next_level", { xp: xpToNext, label: nextLabel(lv?.level ?? 0) })}
      </div>
    </div>
  );
}

const LEVEL_LABELS = ["入門", "N5", "N4", "N3", "N2", "N1"];
function nextLabel(level: number): string {
  return LEVEL_LABELS[Math.min(LEVEL_LABELS.length - 1, level + 1)];
}

function MapIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2zM9 4v14M15 6v14" />
    </svg>
  );
}

function TrophyIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
      <path d="M7 4h10v2h3a1 1 0 0 1 1 1v3a4 4 0 0 1-4 4h-.34A6 6 0 0 1 13 17.92V20h3v2H8v-2h3v-2.08A6 6 0 0 1 7.34 14H7a4 4 0 0 1-4-4V7a1 1 0 0 1 1-1h3V4zm0 4H5v2a2 2 0 0 0 2 2V8zm10 0v4a2 2 0 0 0 2-2V8h-2z" />
    </svg>
  );
}

function ObjectCard({
  to,
  imgSrc,
  fallback,
  title,
  subtitle,
  colorClass,
  disabled,
  compact,
}: {
  to: string;
  imgSrc: string;
  fallback: string;
  title: string;
  subtitle: string;
  colorClass: string;
  disabled?: boolean;
  compact?: boolean;
}) {
  const inner = (
    <motion.div
      whileHover={disabled ? {} : { scale: 1.06, y: -6 }}
      whileTap={disabled ? {} : { scale: 0.96 }}
      className={`relative bg-white/95 backdrop-blur rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl ${compact ? "p-2 sm:p-3" : "p-4 sm:p-5"
        } flex flex-col items-center transition ${disabled ? "opacity-60" : "cursor-pointer"
        }`}
    >
      <div className={`w-full aspect-square rounded-xl sm:rounded-2xl bg-gradient-to-br ${colorClass} mb-2 sm:mb-3 overflow-hidden flex items-center justify-center max-h-56`}>
        <SmartImg
          src={imgSrc}
          alt={title}
          className="w-full h-full object-contain animate-float-soft"
          fallback={
            <div className={`w-full h-full flex items-center justify-center ${compact ? "text-4xl sm:text-6xl" : "text-7xl sm:text-8xl"}`}>
              {fallback}
            </div>
          }
        />
      </div>
      <div className={`${compact ? "text-sm sm:text-base" : "text-xl sm:text-2xl"} font-bold text-primary text-center`}>{title}</div>
      <div className={`${compact ? "text-[10px] sm:text-xs" : "text-sm"} text-muted text-center`}>{subtitle}</div>
    </motion.div>
  );
  if (disabled) return <div>{inner}</div>;
  return <Link to={to}>{inner}</Link>;
}
